// API响应基础接口
export interface BaseResponse<T = any> {
  code: number;
  data: T;
  message: string;
  success: boolean;
}

// 分页参数接口
export interface PaginationParams {
  current: number;
  pageSize: number;
  total?: number;
}

// 分页响应接口
export interface PaginationResponse<T> {
  list: T[];
  pagination: {
    current: number;
    pageSize: number;
    total: number;
  };
}

// 通用请求参数
export interface RequestParams {
  [key: string]: any;
}

// 用户信息接口
export interface UserInfo {
  id: string;
  username: string;
  nickname: string;
  avatar?: string;
  email?: string;
  role: string;
  permissions?: string[];
}

// 登录请求参数
export interface LoginParams {
  username: string;
  password: string;
  remember?: boolean;
}

// 登录响应数据
export interface LoginResult {
  token: string;
  userInfo: UserInfo;
}

// 文章接口
export interface Article {
  id: string;
  title: string;
  content: string;
  cover?: string;
  author: UserInfo;
  createdAt: string;
  updatedAt: string;
  tags?: string[];
  category?: string;
  views?: number;
  likes?: number;
  commentCount?: number;
}

// 评论接口
export interface Comment {
  id: string;
  content: string;
  author: UserInfo;
  createdAt: string;
  parentId?: string;
  replyTo?: string;
}

// 项目接口
export interface Project {
  id: string;
  name: string;
  description: string;
  cover?: string;
  createdBy: UserInfo;
  createdAt: string;
  updatedAt: string;
  members: UserInfo[];
  status: 'active' | 'archived' | 'completed';
}

// API项目参数和响应接口
export interface ProjectListParams {
  status?: 'active' | 'archived' | 'completed';
  page_number?: number;
  page_size?: number;
}

export interface ProjectItem {
  project_id: string;
  name: string;
  description: string;
  cover: string;
  created_by: string;
  created_at: number;
  updated_at: number;
  status: 'active' | 'archived' | 'completed';
}

export interface ProjectListResponse {
  list: ProjectItem[];
  total: number;
}

export interface ProjectDetailParams {
  project_id: string;
}

export interface ProjectMember {
  username: string;
  nickname: string;
  avatar: string;
}

export interface ProjectDetail extends ProjectItem {
  members: ProjectMember[];
}

export interface ProjectDetailResponse {
  project: ProjectDetail;
}

export interface CreateProjectParams {
  name: string;
  description?: string;
  cover?: string;
}

export interface CreateProjectResponse {
  project_id: string;
}

export interface UpdateProjectParams {
  project_id: string;
  name?: string;
  description?: string;
  cover?: string;
  status?: 'active' | 'archived' | 'completed';
}

export interface DeleteProjectParams {
  project_id: string;
}

export interface ProjectMemberParams {
  project_id: string;
  member_id_list?: string[];
  member_id?: string;
}

// 任务接口
export interface Task {
  id: string;
  title: string;
  description: string;
  assignee?: UserInfo;
  project: Project;
  status: 'todo' | 'in_progress' | 'review' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: string;
  createdAt: string;
  updatedAt: string;
}

// 资产接口
export interface Asset {
  id: string;
  name: string;
  type: 'image' | 'video' | 'audio' | 'document' | 'other';
  url: string;
  thumbnail?: string;
  size: number;
  createdBy: UserInfo;
  createdAt: string;
  tags?: string[];
  category?: string;
}

// 创作内容接口
export interface Creation {
  id: string;
  title: string;
  type: 'text' | 'image' | 'video';
  content: string;
  thumbnail?: string;
  status: 'draft' | 'published' | 'archived';
  createdBy: UserInfo;
  createdAt: string;
  updatedAt: string;
  assets?: Asset[];
  tags?: string[];
  category?: string;
}

// 会话消息接口
export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
}

// 会话接口
export interface Conversation {
  id: string;
  title: string;
  messages: ConversationMessage[];
  createdAt: string;
  updatedAt: string;
  projectId?: string;
  createdBy: UserInfo;
  model: string;
}

// 小组类型枚举
export enum GroupType {
  Project = 1,
  Folders = 2,
  Canvas = 3,
  CanvasFolder = 4, // 画布文件夹（资产管理文件夹）
}

// 创建分组请求参数
export interface CreateGroupParams {
  name: string;
  type: GroupType;
}

// 创建分组响应数据
export interface CreateGroupResponse {
  group_id: string;
}

// 获取分组列表请求参数
export interface GetGroupListParams {
  type: GroupType; // 1或2
  need_cover?: boolean; // 新增：是否需要封面，可选
}

// 分组列表项
export interface GroupListItem {
  group_id: string;
  item_count: number;
  name: string;
  update_time: number;
  cover_url?: string;
}

// 分组列表响应数据
export interface GroupListResponse {
  list: GroupListItem[];
}

// 分组重命名请求参数
export interface RenameGroupParams {
  group_id: string;
  name: string;
  cover_asset_id?: string;
  cover_url?: string;
}

// 分组删除请求参数
export interface DeleteGroupParams {
  group_id: string;
  type?: GroupType;
}

// 同步用户Token请求参数
export interface SyncTokenParams {
  token: string;
  customerCode: string;
  aesKey: string;
  code: string;
}

// 同步用户Token响应数据
export interface SyncTokenResponse {
  ticket: string;
  url: string;
}

// 会话分组请求参数
export interface ChatGroupingParams {
  chat_id: string;
  new_group_id: string;
  old_group_id: string;
}

// 创建会话请求参数
export interface CreateChatParams {
  model_id_list: string[];
  group_id?: string;
  name?: string;
  /** 画布 ID（画布节点创建会话时使用） */
  canvas_id?: string;
  /** 节点 ID（画布节点创建会话时使用） */
  node_id?: string;
  /** 创作来源：2=画布 */
  creation_source?: number;
}

// 创建会话响应数据
export interface CreateChatResponse {
  chat_id: string;
}

// 获取会话列表请求参数
export interface ChatListParams {
  group_id?: string;
  page_number?: number;
  page_size?: number;
}

// 会话列表项
export interface ChatListItem {
  chat_id: string;
  group_id: string;
  name: string;
  update_time: number;
}

// 会话列表响应数据
export interface ChatListResponse {
  list: ChatListItem[];
  total?: number;
  has_more?: boolean;
}

// 获取会话消息列表请求参数
export interface ChatFeedListParams {
  chat_id: string;
}

// 会话消息-回答项
export interface ChatMessageAnswer {
  msg_oid: string;
  model_id: string;
  reasoning_content: {
    content: string;
    duration: number;
  };
  result: string;
  create_time: number;
  update_time: number;
}

// 会话消息项
export interface ChatMessageItem {
  msg_iid: string;
  payload: ChatMessagePayload;
  output_list: ChatMessageAnswer[];
}

interface ChatMessagePayload {
  message: Array<{
    content: string;
    role: string;
  }>;
  refs: string[];
  model_id_list: string[];
}

// 会话消息列表响应数据
export interface ChatFeedListResponse {
  list: ChatMessageItem[];
}

// 清空会话历史请求参数
export interface ChatClearParams {
  chat_id: string;
}

// 删除会话请求参数
export interface DeleteChatParams {
  chat_ids: string[];
}

// 会话重命名请求参数
export interface RenameChatParams {
  chat_id: string;
  name: string;
}

export interface SendChatStreamParams {
  chat_id: string;
  model_id_list: string[];
  refs: string[];
  message: Array<{ role: string; content: string }>;
  signal?: AbortSignal;
  need_rename?: boolean;
  reverse_prompt?: boolean;
  /** 画布 ID（画布节点流式生成时使用） */
  canvas_id?: string;
  /** 节点 ID（画布节点流式生成时使用） */
  node_id?: string;
  /** 创作来源：2=画布 */
  creation_source?: number;
  /** 文本引用列表 */
  ref_text?: Array<{ text: string; node_id: string }>;
  /** 参考图片（带节点ID） */
  ref_images_data?: Array<{ asset_id: string; node_id: string }>;
  /** 参考视频（带节点ID） */
  ref_videos_data?: Array<{ asset_id: string; node_id: string }>;
}

export interface ChatInfoResponse {
  ref_count: number;
  video_count: number;
}

// 资产类型枚举
export enum AssetType {
  All = 0,
  Image = 1,
  Video = 2,
  Audio = 3,
  Document = 4,
}

// 收藏状态枚举
export enum FavoriteStatus {
  All = 0, // 全部
  Favorite = 1, // 已收藏
  Unfavorite = 2, // 未收藏
}

// 排序方式枚举
export enum SortOrder {
  TimeDesc = 0, // 时间降序
  TimeAsc = 1, // 时间升序
}

export enum AssetSource {
  All = 0,
  UserUpload = 1, //用户上传
  ModelGenerated = 2, //模型生成
}

// 获取资产列表请求参数
export interface AssetListParams {
  asset_type?: AssetType;
  favorite?: FavoriteStatus;
  group_id?: string;
  model_id?: string;
  page_number?: number;
  page_size?: number;
  time_desc?: SortOrder;
  /**
   * 资产ID列表
   * 当此参数有值时，将忽略其他筛选参数，直接返回指定ID的资产信息
   */
  asset_id_list?: string[];
  source?: AssetSource; // 1=用户上传，2=模型生成
  module?: UploadModule; // 模块类型，用于过滤特定模块上传的资产
  creation_source?: number; // 来源：2=画布生成，-1=历史记录（不限来源）
}

// 资产项
export interface AssetItem {
  asset_id: string;
  msg_iid: string;
  asset_type: number;
  url: string;
  thumb?: string;
  create_time: number;
  update_time: number;
  favorite: FavoriteStatus;
  name?: string; // 文件名
  size?: number; // 文件大小（字节）
  model_id: string;
  duration?: number; // 音频/视频时长（秒）
  module?: number; // 资产归属模块：1=图片创作等业务模块
  source?: number; // 来源：1=用户上传，2=模型生成
  creation_source?: number; // 创作来源：1=上传，2=画布等（/feed/list 详情查询需按 item 原值回传）
  /** 上传者个人账号 id（/v1/asset/list 返回；与当前账户 personal_user_id 比对控制删除/使用显隐） */
  personal_user_id?: string;
}

// 资产列表响应数据
export interface AssetListResponse {
  list: AssetItem[];
  /** 符合筛选条件的资产总数（后端新增字段；旧接口可能不返回，取值需兜底） */
  total?: number;
}

// 资产收藏等操作参数
export interface AssetOperationParams {
  asset_id_list: string[];
  action?: number;
}

// 资产删除请求参数
type ByAssetIds = { asset_id_list: string[]; group_id?: never; msg_oid_list?: never };
type ByGroupId = { group_id: string; asset_id_list?: never; msg_oid_list?: never };
type ByMsgOids = { msg_oid_list: string[]; asset_id_list?: never; group_id?: never };

export type AssetDeleteParams = ByAssetIds | ByGroupId | ByMsgOids;

// 资产下载请求参数
export interface AssetDownloadParams {
  asset_id_list?: string[];
  group_id?: string;
  canvas_id?: string;
  /** 画布批量下载：框选中的节点 id 列表（打组态传底板 id，后端据 canvas_id + 节点定位素材） */
  node_id_list?: string[];
}

// 资产下载响应数据
export interface AssetDownloadResponse {
  url: string;
}

// 资产分组条目
export interface AssetGroupingItem {
  asset_id: string;
  new_group_id?: string;
  old_group_id?: string;
}

// 资产分组请求参数
export interface AssetGroupingParams {
  list: AssetGroupingItem[];
}

// 模块类型枚举
export enum UploadModule {
  Default = 1,
  ImageCreation = 2,
  VideoCreation = 3,
  AudioCreation = 4,
  VideoReference = 6, // 多模态参考视频上传
  Wan30DocumentCreation = 10, // Wan3.0 多模态参考文档上传
}

// 上传来源枚举
export enum UploadSource {
  UserUpload = 1, // 用户上传
  TempResource = 5, // 临时资源（局部重绘、消除笔功能）
}

// 上传文件响应数据
export interface AssetUploadResponse {
  asset_id: string;
  asset_type: number;
  size: number;
  url: string;
  duration?: number; // 音频/视频时长（秒）
}

// 创意模式枚举
export enum CreativeMode {
  Normal = 0,
  Creative = 1,
}

// 参考图类型枚举 0=默认;1=角色特征;2=人物长相
export enum ReferenceType {
  None = 0,
  Variation = 1, // 角色特征
  Reference = 2, // 人物长相
}

// 图片生成请求参数
export interface ImageTaskParams {
  prompt: string;
  model_id_list: string[];
  creative_mode?: CreativeMode;
  image_ratio?: string;
  number?: number;
  ref_images?: string[];
  ref_type?: ReferenceType;
  image_fidelity?: number;
  human_fidelity?: number;
  raw?: number;
  changeDegree?: number;
  stylize?: number;
  // 即梦5.0等模型支持的清晰度和宽高设置
  resolution?: string;  // 图片质量，例如 "2K", "3K"
  width?: number;       // 图片宽度
  height?: number;      // 图片高度
  quality?: string;     // 图像质量，如 "low" | "medium" | "high"（GPT Image 2 等模型支持）
  background?: string;  // 背景，如 "auto" | "transparent" | "opaque"（gpt-image-2.5-flare 等模型支持）
  thinking_level?: string; // 思考等级，如 "minimal" | "medium" | "high"（nano-banana 系列模型支持）
  // 局部重绘遮罩图 asset_id
  mask_image?: string;
  meta_list?: Array<{
    meta_type: 'text' | 'image' | 'video' | 'audio';
    text?: string;
    material_ref?: { material_idx: number };
  }>;
  // Skill 模式字段
  skill_id?: string;
  skill_ids?: string[];
  // 画布相关字段
  canvas_id?: string;
  node_id?: string;
  /** 文本引用列表，第一位为创作框本地输入，后续为上游文本节点 */
  ref_text?: Array<{ text: string; node_id: string }>;
  /** 图片引用列表（带来源节点 ID），与 ref_images 并存 */
  ref_images_data?: Array<{ asset_id: string; node_id: string }>;
  /** 相机效果（开启时传入，关闭时不传） */
  camera?: {
    device: string;
    lens: string;
    focal_length: number;
    aperture: number;
  };
  /** 参考视频（带节点ID，画布扩展） */
  ref_videos_data?: Array<{ asset_id: string; node_id: string }>;
  /** 模型私有参数集合（GPT Image 2.5 系列的 background 等在此下发） */
  params?: {
    /** GPT Image 2.5 参数：auto（自动识别，默认）、transparent（透明）、opaque（不透明） */
    background?: string;
  };
}

// 视频生成请求参数
export interface VideoTaskParams {
  prompt: string;
  model_id_list: string[];
  tail_image?: string;
  duration?: number;
  number?: number;
  ref_images?: string[];
  ref_images_url?: string[];
  tail_image_url?: string;
  human_fidelity?: number;
  image_fidelity?: number;
  mode_type: number; //用于区分参考图和首尾帧 1:参考图 (默认) 2:首尾帧 3:多模态参考
  resolution?: string;
  aspect_ratio?: string;
  sound?: string;
  reference_mode?: number; // 参考模式标识：1 首尾帧, 2 多模态参考
  ref_videos?: string[]; // 多模态参考视频ID列表
  ref_videos_url?: string[]; // 多模态参考视频URL列表
  ref_audios?: string[]; // 多模态参考音频ID列表
  /** 多模态参考文档（Wan3.0）：asset_type=Link 传 url，asset_type=File 传 asset_id */
  media?: Array<{ asset_type: 'Link' | 'File'; url?: string; asset_id?: string }>;
  real_video?: boolean;
  head_image?: string; // 首帧图片 asset_id
  // ── 画布扩展字段（与图片生成保持一致）──────────────────────────────────────
  canvas_id?: string;
  node_id?: string;
  /** 文本引用列表，第一位为创作框本地输入 */
  ref_text?: Array<{ text: string; node_id: string }>;
  /** 参考图片（带节点ID，asset_id 版） */
  ref_images_data?: Array<{ asset_id: string; node_id: string }>;
  /** 参考图片（带节点ID，URL 版） */
  ref_images_url_data?: Array<{ url: string; node_id: string }>;
  /** 尾帧图片（带节点ID，asset_id 版） */
  tail_image_data?: Array<{ asset_id: string; node_id: string }>;
  /** 尾帧图片（带节点ID，URL 版，注意 API 字段名拼写 tail_imag_url_data） */
  tail_imag_url_data?: Array<{ url: string; node_id: string }>;
  /** 参考视频（带节点ID） */
  ref_videos_data?: Array<{ asset_id: string; node_id: string }>;
  /** 参考音频（带节点ID） */
  ref_audios_data?: Array<{ asset_id: string; node_id: string }>;
}

// 生成任务查询参数
export interface TaskQueryParams {
  msg_iid: string;
}

// 任务创建响应数据
export interface TaskCreateResponse {
  msg_iid: string;
  /** Skill 组图任务返回的父任务ID，用于查询子任务列表 */
  parent_msg_iid?: string;
  creation_mode: number;
  list: {
    model_id: string;
    eta?: number; // 预计等待时间（分钟）
  }[];
}

// 生成任务结果-资产项
export interface GeneratedAssetItem {
  asset_id: string;
  asset_type?: number; // 资产类型；2:视频
  msg_oid?: string; // 单条任务/资产消息ID，失败任务删除时使用
  url: string;
  cover_url?: string; // 封面URL
  task_status: number; //任务状态;3:成功；4:失败
  eta?: number; // 预计等待时间（分钟）
  favorite?: number; // 收藏状态;2:未收藏；1:已收藏
  error_msg?: string;
}

// 生成任务结果项
export interface TaskResultItem {
  model_id: string;
  eta?: number; // 预计等待时间（分钟）
  images?: GeneratedAssetItem[];
  videos?: GeneratedAssetItem[];
  /** 文本类型 Skill 子任务返回的文本结果（如脚本、提示词等） */
  result?: string;
  text?: string;
}

// 任务查询响应数据
export interface TaskQueryResponse {
  list: TaskResultItem[];
}

// 模型信息接口
export interface ModelInfo {
  model_id: string;
  model_name: string;
  model_type?: string;
  model_display_name: string;
  type_tags: string[];
  vendor_name?: string;
  free: boolean;
  group_sort: number;
  disable?: boolean; // 模型是否被禁用
  ext_info?: {
    model_logo?: string;
    model_description?: string;
    support_file_types?: string[]; // 支持的文件类型扩展名，如 ['pdf', 'docx', 'png', 'jpeg', 'webp']
    support_video?: boolean; // 是否支持视频上传
    type_tags?: string[]; // 模型标签
    is_free?: boolean;
    disable_intersection?: boolean; // 是否互斥单选（不参与多模型取交集）
    /** 模型所在页（模型选择器分页用，图片模型常放在 field_map.page，兼容此处） */
    page?: number;
    /** 模型字段约束（来自 /model/list 接口的 ext_info.field_map；视频用 VideoFieldMap，图片附加分页 page） */
    field_map?: VideoFieldMap & { page?: number };
  };
}

/** 视频生成模型字段约束 */
export interface VideoFieldMap {
  aspect_ratio?: { min: number; max: number };
  duration?: { min: number; max: number; total_max?: number };
  ref_images?: {
    label?: string;
    min?: number;
    max?: number;
    name?: string;
    type?: string;
    formats?: string[];
    aspect_ratio?: { min: number; max: number };
    dimension?: { min: number; max: number };
    max_file_size_mb?: number;
  };
  ref_videos?: {
    label?: string;
    min?: number;
    max?: number;
    name?: string;
    type?: string;
    formats?: string[];
    resolution?: string[];
    duration?: { min: number; max: number; total_max?: number };
    aspect_ratio?: { min: number; max: number };
    dimension?: { min: number; max: number };
    pixel_count?: { min: number; max: number };
    fps?: { min: number; max: number };
    max_file_size_mb?: number;
  };
  ref_audios?: {
    label?: string;
    min?: number;
    max?: number;
    name?: string;
    type?: string;
    formats?: string[];
    duration?: { min: number; max: number; total_max?: number };
    max_file_size_mb?: number;
  };
  [key: string]: any;
}

// 模型列表响应数据
export interface ModelListResponse {
  list: ModelInfo[];
}

// Feed流列表请求参数
export interface FeedListParams {
  creation_type_list?: number[];
  page_number?: number;
  page_size?: number;
  msg_iid_list?: string[];
  last_id?: string; // 上一次请求返回的 last_id，用于分页
  creation_source?: number; // 来源过滤：2=画布生成
}

// Feed流图片载荷
export interface FeedImagePayload {
  creative_mode: number;
  image_ratio: string;
  model_id_list: string[];
  number?: number;
  prompt: string;
  ref_images: string[];
  ref_type: number;
  image_fidelity?: number;
  human_fidelity?: number;
  // 即梦4.5等模型支持的清晰度和宽高设置
  resolution?: string;  // 例如 "2K", "4K"
  quality?: string;     // 例如 "low", "medium", "high"
  width?: number;       // 图片宽度
  height?: number;      // 图片高度
  // 思考等级（nano-banana 系列：minimal/medium/high）
  thinking_level?: string;
  // 多模态模式的 meta_list，用于从列表编辑回显 @mention 图片
  meta_list?: Array<{
    meta_type: 'text' | 'image' | 'video' | 'audio';
    text?: string;
    material_ref?: { material_idx: number };
  }>;
}

// Feed流视频载荷
export interface FeedVideoPayload {
  duration: number;
  model_id_list: string[];
  number: number;
  prompt: string;
  ref_images: string[];
  tail_image: string;
  mode_type: number; //用于区分参考图和首尾帧 1:参考图 (默认) 2:首尾帧 3:多模态参考
  resolution?: string;
  aspect_ratio?: string;
  sound?: string;
  ref_videos?: string[]; // 多模态参考模式的视频引用ID
  ref_videos_url?: string[]; // 多模态参考模式的视频引用URL（示例图片）
  ref_audios?: string[]; // 多模态参考模式的音频引用ID
  /** 多模态参考文档（Wan3.0）：asset_type=Link 传 url，asset_type=File 传 asset_id */
  media?: Array<{ asset_type: 'Link' | 'File'; url?: string; asset_id?: string }>;
  meta_list?: Array<{
    meta_type: 'text' | 'image' | 'video' | 'audio';
    text?: string;
    material_ref?: { material_idx: number };
  }>; // 多模态模式下的结构化内容列表
  real_video?: boolean; // 真人视频开关（仅 Seedance 2.0）
}

// Feed流通用载荷
export interface FeedPayload {
  model_id_list: string[];
  number: number;
  arguments: any;
}
// Feed流输出项
export interface FeedOutputItem {
  assets: GeneratedAssetItem[];
  model_id: string;
  eta?: number; // 预计等待时间（分钟）
  real_number?: number; // 实际生成数量
}

// Feed流列表项
export interface FeedItem {
  output_list: FeedOutputItem[];
  expect_model_num?: number;
  creation_type: number;
  creation_mode?: number;
  image_payload?: FeedImagePayload;
  msg_iid: string;
  video_payload?: FeedVideoPayload;
  payload?: FeedPayload;
}

// Feed流列表响应
export interface FeedListResponse {
  list: FeedItem[];
  last_id?: string; // 当前分页的最后一条记录ID，用于下次请求
}

export enum ProductType {
  SinglePurchase = 1, // 单次购买
  MonthlyPurchase = 2, // 单月购买
  YearlyPurchase = 3, // 单年购买
  MonthlySubscription = 4, // 包月购买
  YearlySubscription = 5, // 包年购买
  ThreeMonthPackage = 6, // 3个月套餐
  TwelveMonthPackage = 7, // 12个月套餐
}

export interface FeatureSwitchItem {
  status: number;
  begin_time: string;
  end_time: string;
}

export type CampaignStatus = Record<string, FeatureSwitchItem>;

export interface ProductListParams {
  type: ProductType;
  price_asc?: boolean;
}

export interface ProductListItem {
  name: string;
  biz_code: string;
  package_id: string;
  type: ProductType;
  original_price: number;
  current_price: number;
  exchange_credits: number;
  bonus_credits?: number;
  member_level?: number;
  member_level_name?: string; // 会员等级名称
  valid_days?: number;
  desc?: string;
  per_100_desc?: string; // 每100米粒价格描述
  per_month_desc?: string; // 每月价格描述
  rights: {
    name: string;
  }[];
}

export interface ProductListResponse {
  list: ProductListItem[];
}

// 积分计算模型详情
export interface CreditCalculationItem {
  llm_id: string;
  credits: number;
  number: number;
  unit_credits: number; // 单张消耗的积分
}

// 积分计算响应数据
export interface CreditCalculationResponseData {
  list: CreditCalculationItem[];
  total_credits: number;
  user_total_credits: number;
  original_total_credits?: number;
}

// 积分计算接口完整响应
export interface CreditCalculationResponse {
  code: number;
  data: CreditCalculationResponseData;
  msg: string;
}

// 米粒值消耗明细项（用于UI显示）
export interface CreditDetailItem {
  modelId: string;
  modelName: string;
  credits: number;
  quantity: number;
  unitPrice: number; // 单张消耗的积分（来自API的unit_credits字段）
  dailyFreeCount?: number; // 每日免费次数
}

// 米粒值消耗明细数据（用于UI组件）
export interface CreditDetailData {
  items: CreditDetailItem[];
  totalCredits: number;
  userTotalCredits: number;
  originalTotalCredits?: number; // 折前总消耗积分
}

// 订单相关接口定义
export interface CreateOrderParams {
  package_id: string;
  full_price?: number;
}

export interface CreateOrderResponse {
  order_id: string;
  ordered_at: string;
  expired_at: string;
  payable_amount: number;
  code_url: string;
  tip_info?: {
    title: string;
    tips: string[];
  };
}

export interface QueryOrderParams {
  order_id: string;
}

export interface QueryOrderResponse {
  order_id: string;
  ordered_at: string;
  expired_at: string;
  payable_amount: number;
  code_url: string;
  pay_status: number; // 1=待支付；2=已支付；3=已取消；4=已退款
}

// 用户积分流水相关接口定义
export interface UserCreditsParams {
  need_record?: boolean; // 是否需要查询积分历史；默认 false
  record_type?: number; // 流水类型（0=全部；2=购买；1=消耗；3=获得）
  page_number?: number; // 页码；默认为1
  page_size?: number; // 页条；[20,100],默认为20
}

export interface CreditRecord {
  biz_name: string; // 业务名称
  record_type: number; // 流水类型（1=购买；2=消耗；3=获得）
  trans_time: string; // 交易时间
  credits: number; // 积分
}

export interface UserCreditsResponse {
  list: CreditRecord[];
  total_credits: number; // 总积分余额（冗余字段，member + purchased + gift）
  member_credits: number; // 会员积分余额
  purchased_credits: number; // 购买积分余额
  gift_credits: number; // 赠送积分余额
}

// 用户权益相关接口定义
export interface UserBenefitsParams {
  // 空对象，前端发起接口调用
}

export interface BenefitItem {
  type: number; // 权益类型：1=首次注册赠送米粒值；2=会员每日登录赠送米粒值；3=会员每月登录赠送米粒值
  credits: number; // 积分值
}

// 权益类型枚举
export enum BenefitType {
  WELCOME_GIFT = 1, // 首次注册赠送米粒值
  DAILY_REWARD = 2, // 会员每日登录赠送米粒值
  MONTHLY_REWARD = 3, // 会员每月登录赠送米粒值
}

export interface UserBenefitsResponse {
  list: BenefitItem[]; // 发放的权益列表
}

// 用户套餐相关接口定义
export interface UserPackageParams {
  // 空对象
}

export interface UserPackageItem {
  package_name: string; // 套餐名称
  start_time: string; // 生效时间
  end_time: string; // 失效时间
}

export interface UserPackageResponse {
  list: UserPackageItem[]; // 已购买的套餐列表
  free_package: {
    rights: string[]; // 权益列表
  };
}

// AMAI 新增接口类型定义

// 更新手机号请求参数
export interface UpdatePhoneParams {
  mobile: string; // 手机号
  country_code?: string; // 国家区号：86
  sms_code: string; // 短信验证码
}

// 更新用户资料请求参数（更新微信号、手机号、昵称、个人简介）
export interface UpdateProfileParams {
  wechat?: string; // 微信号
  mobile?: string; // 手机号
  sms_code?: string; // 短信验证码（更新手机号时需要）
  user_name?: string; // 昵称
  personal_profile?: string; // 个人简介
}

// 企业入驻请求参数
export interface CompanyAuthApplyParams {
  company_name: string; // 企业全称
  business_license: string; // 企业营业执照（asset_id）
  company_email: string; // 企业邮箱
  contact_name: string; // 联系人姓名
  contact_mobile: string; // 联系电话
  sms_code: string; // 验证码
}

// 创作者认证申请资产
export interface CreatorAuthApplyAsset {
  asset_id: string; // 资产ID
  file_name: string; // 文件名
}

// 创作者认证申请请求参数
export interface CreatorAuthApplyParams {
  wechat: string; // 微信号
  user_name: string; // 昵称
  personal_profile: string; // 个人简介
  apply_asset: CreatorAuthApplyAsset[]; // 申请资产列表
  mobile: string; // 手机号
  sms_code: string; // 验证码
}

// 创作者认证申请响应数据
export interface CreatorAuthApplyResponse {
  apply_status: number; // 申请状态
  token?: string; // 用户Token
}

// 发送短信验证码请求参数
export interface SendSmsCodeParams {
  mobile: string; // 手机号
  country_code?: string; // 国家区号：86
  type: number; // 类型：1=更新手机号；2=登录
}

// 短信验证码登录请求参数
export interface SmsLoginParams {
  mobile: string; // 手机号
  country_code?: string; // 国家区号：86
  sms_code: string; // 短信验证码（注意：文档中是sms——code，这里修正为sms_code）
  source:number; //来源 1 h5 2 pc
}

// 账号密码登录请求参数
export interface PasswordLoginParams {
  auth_identifier: string; // 用户名
  auth_credential: string; // 密码
  auth_type: number; // 登录类型
}

// 账号密码登录响应数据
export interface PasswordLoginResponse {
  token: string; // 用户Token
  user_avatar?: string; // 用户头像URL
  user_id: string; // 用户ID
  user_name?: string; // 用户名称
  mobile: string; // 用户手机号
  auth_identifier?: string; // 用户名
}

// 短信验证码登录响应数据
export interface SmsLoginResponse {
  token: string; // 用户Token
  user_avatar?: string; // 用户头像URL
  user_id: string; // 用户ID
  user_name?: string; // 用户名称
  mobile: string; // 用户手机号
}

// 基础API响应接口（AMAI格式）
export interface AmaiApiResponse<T = any> {
  code: number;
  data?: T;
  msg: string;
}

// 短信验证码类型枚举
export enum SmsCodeType {
  UPDATE_PHONE = 1, // 更新手机号
  LOGIN = 2, // 登录
}

// 小工具任务参数接口
export interface ToolsTaskParams {
  model_id_list: string[];
  number: number;
  arguments: Array<{ field_name: string; field_value: string | number | boolean }>;
  // 画布相关字段（扩图/多角度等 /task/submit 任务由画布发起时携带）
  canvas_id?: string;
  node_id?: string;
}

// 小工具任务响应数据接口
export interface ToolsTaskResponseData {
  list: Array<{
    llm_id: string;
    unit_credits: number;
    credits: number;
    number: number;
    daily_free_count: number;
  }>;
  total_credits: number;
  user_total_credits: number;
}

// 小工具任务响应接口
export interface ToolsTaskResponse {
  code: number;
  data: ToolsTaskResponseData;
  msg: string;
}

// 小工具任务提交响应数据接口
export interface ToolsTaskSubmitResponseData {
  msg_iid: string;
  creation_mode?: number;
  list: Array<{
    model_id: string;
    eta: number;
  }>;
}

// 小工具任务提交响应接口
export interface ToolsTaskSubmitResponse {
  code: number;
  data: ToolsTaskSubmitResponseData;
  msg: string;
}
// 翻译接口
export interface TranslateParam {
  country_code: string;
  raw_content: string;
}

// 用户中心相关接口定义

// 用户信息响应数据
export interface UserInfoResponse {
  user_id: string;
  user_name: string;
  user_avatar?: string;
  mobile: string;
  wechat_id?: string;
  bio?: string;
}

// 获取用户信息请求参数
export interface GetUserInfoParams {
  // 空对象
}

// 创作任务状态枚举
export enum CreationTaskStatus {
  All = 0, // 全部状态
  InProgress = 1, // 进行中
  DeliverablesUploaded = 2, // 已上传交付物
  AwaitingModification = 3, // 待修改交付物
  Completed = 4, // 已完成
  Overdue = 5, // 已逾期
}

// 创作任务项
export interface CreationTaskItem {
  task_id: string; // 任务ID
  task_name: string; // 任务名称
  task_desc: string; // 任务描述
  created_at: string; // 创建日期
  real_finish_date?: string; // 完成日期
  target_finish_date: string; // 任务截止日期
  status: CreationTaskStatus; // 当前状态
  resources_name?: string; // 参考资料包名称
  resources_url?: string; // 参考资料包下载地址
  deliverables_name?: string; // 交付物名称
  deliverables_url?: string; // 交付物下载地址
  commission_amount?: number; // 报酬金额（单位：元）
}

// 获取创作任务列表请求参数
export interface GetCreationTaskListParams {
  status?: CreationTaskStatus; // 任务状态筛选，0表示全部
  page_number?: number;
  page_size?: number;
}

// 创作任务列表响应数据
export interface CreationTaskListResponse {
  list: CreationTaskItem[];
  total: number;
}

// 任务大厅接口请求参数
export interface GetTaskHallParams {
  status?: string; // 状态：0全部 1进行中 4已完成，string类型
  page_number?: number; // 页码，number类型
  page_size?: string; // 每页查询条数，string类型
}
export interface GetTaskHallParamsNew {
  project_signup_status?: string; // 状态：0全部 1进行中 4已完成，string类型
  page_number?: number; // 页码，number类型
  page_size?: string; // 每页查询条数，string类型
}

// 任务大厅任务项
export interface TaskHallItem {
  id: number;
  project_id: string;
  project_image: string;
  project_name: string;
  project_desc: string;
  project_tag: string[];
  status: number;
  total_amount?: number; // 总金额（单位：元）
}

export interface TaskHallItemNew {
  id: number;
  project_id: string;
  project_image: string;
  project_name: string;
  project_desc: string;
  project_tag: string[];
  project_signup_status: number;
  user_signup_status: number;
  show_price: number;
  total_amount?: number; // 总金额（单位：元）
}

// 任务大厅响应数据
export interface TaskHallResponse {
  list: TaskHallItem[];
  total: number;
}

// 上传交付物请求参数
export interface UploadDeliverablesParams {
  task_id: string;
  file_list: string[]; // 文件URL列表或asset_id列表
}

// 上传交付物文件请求参数
export interface UploadDeliverablesFileParams {
  file: File; // 要上传的文件
  task_id: string; // 任务ID
}

// 视频编辑器任务参数接口
export interface EditorProjectParams {
    TemplateType: string;
    PageSize: number;
    PageNo: number;
}

// 视频编辑器任务响应数据接口
export interface EditorProjectResponseData {
    TotalCount: number;
    ProjectList: Array<{
        ProjectId: string;
        Title: string;
        CreateTime: string;
        Status: string;
    }>;
}

// 视频编辑器任务响应数据接口
export interface EditorProjectResponse {
    code: number;
    data: EditorProjectResponseData;
    msg: string;
}

// ───────────── 团队空间（V0.1 团队协作，契约《20261023团队协作v0.1后端技术方案》）─────────────

/** 账户类型（契约 account_type） */
export enum AccountType {
  /** 个人账号 */
  Personal = 1,
  /** 团队账号 */
  Team = 2,
}

/** 账户列表项（个人账户与团队账户共用结构） */
export interface AccountListItem {
  user_avatar: string;
  user_id: string;
  user_name: string;
  mobile: string;
  auth_identifier: string;
  personal_profile: string;
  wechat: string;
  auth_level: string;
  apply_auth: number;
  is_member: boolean;
  /** 剩余积分，单位分（1米粒 = 10分），展示需 /10 */
  total_credits: number;
  account_type: AccountType;
  /** 仅团队账户有：团队角色 1-拥有者 2-成员 */
  team_role?: 1 | 2;
}

/** 账户列表响应（POST /v1/user/account_list） */
export interface AccountListResponse {
  personal_account: AccountListItem;
  team_account_list: AccountListItem[];
}

/** 交换 token 响应（POST /v1/auth/token/exchange；团队 token 前缀 t_） */
export interface TokenExchangeResponse {
  token: string;
  /** 交换后账户类型 1-个人 2-团队 */
  account_type?: AccountType;
  /** 交换响应同时返回个人 user_id（切回个人的交换 target 用，顺手持久化） */
  personal_user_id?: string;
  /** 团队角色 1-拥有者 2-成员（个人空间无） */
  team_role?: 1 | 2;
}
